/** Season helpers for the multi-season event calendar.
 *
 * Builds `events` spans several seasons once historical/future events are
 * imported (S3 … S7), so every race-ops picker needs a single, consistent
 * notion of the "current" season to default to — and a stable way to group by
 * season. Both live here so the sidebar switcher, Admin Events, and any other
 * picker share one definition rather than each re-deriving it. */

interface SeasonEvent {
  season: number;
  start_date: string; // YYYY-MM-DD (lexical compare == chronological)
  end_date: string; // YYYY-MM-DD
}

/** The season to default a picker to.
 *
 * It is the season of the next event that has not finished yet (an ongoing or
 * upcoming event); if the whole calendar is in the past, the season of the most
 * recently finished event; on an empty calendar, undefined.
 *
 * Deliberately NOT `Math.max(season)`: once a future season (e.g. S7) is
 * imported, the max jumps to a season that has not started, which is the wrong
 * default. Dates, not the highest number, decide what "current" means. */
export function currentSeason(
  events: readonly SeasonEvent[],
): number | undefined {
  if (events.length === 0) return undefined;
  // ISO date; sliced to YYYY-MM-DD so it compares lexically against the fields.
  const today = new Date().toISOString().slice(0, 10);

  let nextUpcoming: SeasonEvent | undefined;
  let mostRecentPast: SeasonEvent | undefined;
  for (const e of events) {
    if (e.end_date >= today) {
      if (!nextUpcoming || e.start_date < nextUpcoming.start_date)
        nextUpcoming = e;
    } else if (!mostRecentPast || e.end_date > mostRecentPast.end_date) {
      mostRecentPast = e;
    }
  }
  return (nextUpcoming ?? mostRecentPast)?.season;
}

/** Where an event sits relative to today: finished ("past"), happening right
 * now ("ongoing"), or not started yet ("future"). Pickers use this to render
 * past events muted and mark the ongoing one — same lexical ISO-date compare
 * as `currentSeason`. */
export function eventTiming(
  e: Pick<SeasonEvent, "start_date" | "end_date">,
  today: string = new Date().toISOString().slice(0, 10),
): "past" | "ongoing" | "future" {
  if (e.end_date < today) return "past";
  return e.start_date <= today ? "ongoing" : "future";
}

/** Events grouped by season, seasons newest-first, events within a season
 * newest-first by start_date. */
export function groupBySeason<T extends SeasonEvent>(
  events: readonly T[],
): { season: number; events: T[] }[] {
  const bySeason = new Map<number, T[]>();
  for (const e of events) {
    const arr = bySeason.get(e.season);
    if (arr) arr.push(e);
    else bySeason.set(e.season, [e]);
  }
  return [...bySeason.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([season, evs]) => ({
      season,
      events: [...evs].sort((a, b) => b.start_date.localeCompare(a.start_date)),
    }));
}
